// src/api/finance.api.js
import { apiFetch, apiFetchBlob, downloadBlob } from './core.js'

/**
 * Finance API (FEDIS)
 * -------------------
 * Convention:
 *  - summary: agrégats (CA, achats, dépenses, résultat, etc.)
 *  - journal: événements (vente/achat/dépense/paiement...) sur une période
 *  - export: CSV/PDF
 *
 * Endpoints attendus côté backend:
 *  - GET /api/finance/summary?from=YYYY-MM-DD&to=YYYY-MM-DD
 *  - GET /api/finance/journal?from=YYYY-MM-DD&to=YYYY-MM-DD&limit=50&offset=0&type=...&q=...
 *  - GET /api/finance/accounts?from=...&to=...              (optionnel, utilisé par FinanceAccounts.jsx)
 *  - GET /api/finance/trial-balance?from=...&to=...         (optionnel, utilisé par TrialBalance.jsx)
 *  - GET /api/finance/export?from=...&to=...&type=summary|journal|accounts|trial-balance&format=csv|pdf
 */

/** ✅ Synthèse agrégée */
export async function fetchFinanceSummary({ from, to } = {}) {
  const params = new URLSearchParams()
  if (from) params.set('from', from)
  if (to) params.set('to', to)

  const qs = params.toString()
  return apiFetch(`/api/finance/summary${qs ? `?${qs}` : ''}`)
}

/** ✅ Journal (liste d'événements financiers) */
export async function fetchFinanceJournal({ from, to, limit = 50, offset = 0, type, q } = {}) {
  const params = new URLSearchParams()
  if (from) params.set('from', from)
  if (to) params.set('to', to)
  if (type) params.set('type', type)
  if (q) params.set('q', q)
  params.set('limit', String(limit))
  params.set('offset', String(offset))

  return apiFetch(`/api/finance/journal?${params.toString()}`)
}

/**
 * ✅ Export (CSV/PDF)
 * - Le back doit renvoyer un binaire (content-type: text/csv ou application/pdf)
 */
export async function exportFinance({ from, to, type = 'summary', format = 'csv' } = {}) {
  const params = new URLSearchParams()
  if (from) params.set('from', from)
  if (to) params.set('to', to)
  params.set('type', type)
  params.set('format', format)

  const blob = await apiFetchBlob(`/api/finance/export?${params.toString()}`)
  const filename = `finance-${type}-${from || 'from'}-${to || 'to'}.${format}`
  downloadBlob(blob, filename)

  // utile si un jour tu veux tester sans télécharger
  return { blob, filename }
}

export async function fetchFinanceLedger({
  from,
  to,
  account_code,
  account_id,
  limit = 100,
  offset = 0,
} = {}) {
  const params = new URLSearchParams()
  if (from) params.set('from', from)
  if (to) params.set('to', to)
  if (account_code) params.set('account_code', account_code)
  if (account_id) params.set('account_id', account_id)
  params.set('limit', String(limit))
  params.set('offset', String(offset))
  return apiFetch(`/api/finance/ledger?${params.toString()}`)
}

export async function fetchFinanceCashflow({ from, to } = {}) {
  const params = new URLSearchParams()
  if (from) params.set('from', from)
  if (to) params.set('to', to)
  return apiFetch(`/api/finance/cashflow?${params.toString()}`)
}

export async function fetchFinanceProfitLoss({ from, to } = {}) {
  const params = new URLSearchParams()
  if (from) params.set('from', from)
  if (to) params.set('to', to)
  return apiFetch(`/api/finance/profit-loss?${params.toString()}`)
}

export async function fetchFinancePeriods() {
  return apiFetch('/api/finance/periods')
}

export async function createFinancePeriod(payload) {
  return apiFetch('/api/finance/periods', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload || {}),
  })
}

export async function closeFinancePeriod(id) {
  return apiFetch(`/api/finance/periods/${id}/close`, { method: 'POST' })
}

export async function reopenFinancePeriod(id) {
  return apiFetch(`/api/finance/periods/${id}/reopen`, { method: 'POST' })
}

export async function syncFinance({ from, to } = {}) {
  const params = new URLSearchParams()
  if (from) params.set('from', from)
  if (to) params.set('to', to)
  return apiFetch(`/api/finance/sync?${params.toString()}`, { method: 'POST' })
}

export async function fetchFinanceAudit({ from, to } = {}) {
  const params = new URLSearchParams()
  if (from) params.set('from', from)
  if (to) params.set('to', to)
  return apiFetch(`/api/finance/audit?${params.toString()}`)
}

export async function fetchFinanceVatReport({ from, to } = {}) {
  const params = new URLSearchParams()
  if (from) params.set('from', from)
  if (to) params.set('to', to)
  return apiFetch(`/api/finance/vat-report?${params.toString()}`)
}

export async function createFinanceEntry(payload) {
  return apiFetch('/api/finance/entries', {
    method: 'POST',
    body: payload,
  })
}
